interface VideoPreloadOptions {
  priority?: 'high' | 'medium' | 'low';
  preloadAmount?: 'none' | 'metadata' | 'auto';
  connectionThreshold?: 'slow' | 'fast' | 'any';
}

interface VideoCache {
  [url: string]: {
    blob?: Blob;
    objectUrl?: string;
    isLoading: boolean;
    error?: Error;
    lastAccessed: number;
  };
}

class VideoPreloader {
  private cache: VideoCache = {};
  private maxCacheSize = 75 * 1024 * 1024; // 75MB cache limit - balanced
  private currentCacheSize = 0;
  private connectionSpeed: 'slow' | 'fast' = 'fast';
  private activeDownloads = 0;
  private maxConcurrentDownloads = 2; // Limit concurrent downloads

  constructor() {
    this.detectConnectionSpeed();
    this.setupCacheCleanup();
  }

  private detectConnectionSpeed() {
    // Use Network Information API if available
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      if (connection) {
        const effectiveType = connection.effectiveType;
        this.connectionSpeed = ['slow-2g', '2g', '3g'].includes(effectiveType) ? 'slow' : 'fast';
        
        // Listen for connection changes
        connection.addEventListener('change', () => {
          const newEffectiveType = connection.effectiveType;
          this.connectionSpeed = ['slow-2g', '2g', '3g'].includes(newEffectiveType) ? 'slow' : 'fast';
        });
      }
    }
  }

  private setupCacheCleanup() {
    // Clean up cache every 5 minutes
    setInterval(() => {
      this.cleanupCache();
    }, 5 * 60 * 1000);
  }

  private cleanupCache() {
    const now = Date.now();
    const maxAge = 10 * 60 * 1000; // 10 minutes

    Object.keys(this.cache).forEach(url => {
      const entry = this.cache[url];
      if (now - entry.lastAccessed > maxAge) {
        this.removeFromCache(url);
      }
    });
  }

  private removeFromCache(url: string) {
    const entry = this.cache[url];
    if (entry?.objectUrl) {
      URL.revokeObjectURL(entry.objectUrl);
    }
    if (entry?.blob) {
      this.currentCacheSize -= entry.blob.size;
    }
    delete this.cache[url];
  }

  private shouldPreload(options: VideoPreloadOptions): boolean {
    const { connectionThreshold = 'any', priority = 'medium' } = options;

    // Don't preload on slow connections unless high priority
    if (this.connectionSpeed === 'slow' && connectionThreshold === 'fast') {
      return priority === 'high';
    }

    // Check if user prefers reduced data usage
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      if (connection?.saveData) {
        return priority === 'high';
      }
    }

    return true;
  }

  async preloadVideo(url: string, options: VideoPreloadOptions = {}): Promise<string | null> {
    // Return cached version if available
    if (this.cache[url]?.objectUrl) {
      this.cache[url].lastAccessed = Date.now();
      return this.cache[url].objectUrl!;
    }

    // Check if already loading
    if (this.cache[url]?.isLoading) {
      return new Promise((resolve) => {
        const checkLoaded = () => {
          if (this.cache[url]?.objectUrl) {
            resolve(this.cache[url].objectUrl!);
          } else if (this.cache[url]?.error) {
            resolve(null);
          } else {
            setTimeout(checkLoaded, 100);
          }
        };
        checkLoaded();
      });
    }

    // Check if we should preload
    if (!this.shouldPreload(options)) {
      return null;
    }

    // Check concurrent download limit
    if (this.activeDownloads >= this.maxConcurrentDownloads) {
      console.log('⏳ Download queue full, deferring video preload');
      return null;
    }

    // Initialize cache entry
    this.cache[url] = {
      isLoading: true,
      lastAccessed: Date.now()
    };

    this.activeDownloads++;

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch video: ${response.status}`);
      }

      const blob = await response.blob();
      
      // Check cache size limit
      if (this.currentCacheSize + blob.size > this.maxCacheSize) {
        // Remove oldest entries to make space
        const sortedEntries = Object.entries(this.cache)
          .filter(([_, entry]) => entry.blob)
          .sort(([_, a], [__, b]) => a.lastAccessed - b.lastAccessed);

        for (const [oldUrl] of sortedEntries) {
          this.removeFromCache(oldUrl);
          if (this.currentCacheSize + blob.size <= this.maxCacheSize) {
            break;
          }
        }
      }

      const objectUrl = URL.createObjectURL(blob);
      this.cache[url] = {
        blob,
        objectUrl,
        isLoading: false,
        lastAccessed: Date.now()
      };

      this.currentCacheSize += blob.size;
      this.activeDownloads--;
      return objectUrl;

    } catch (error) {
      this.cache[url] = {
        isLoading: false,
        error: error as Error,
        lastAccessed: Date.now()
      };
      this.activeDownloads--;
      console.warn('Video preload failed:', error);
      return null;
    }
  }

  preloadVideosByTheme(videos: { dark: string; light: string }[], isDark: boolean, options: VideoPreloadOptions = {}) {
    const currentThemeVideos = videos.map(v => isDark ? v.dark : v.light);
    const otherThemeVideos = videos.map(v => isDark ? v.light : v.dark);

    // Preload current theme videos with higher priority
    currentThemeVideos.forEach(url => {
      this.preloadVideo(url, { ...options, priority: 'high' });
    });

    // Preload other theme videos with lower priority if connection is good
    if (this.connectionSpeed === 'fast') {
      setTimeout(() => {
        otherThemeVideos.forEach(url => {
          this.preloadVideo(url, { ...options, priority: 'low' });
        });
      }, 500); // Reduced delay for faster preloading
    }
  }

  getCachedVideoUrl(url: string): string | null {
    const entry = this.cache[url];
    if (entry?.objectUrl) {
      entry.lastAccessed = Date.now();
      return entry.objectUrl;
    }
    return null;
  }

  clearCache() {
    Object.keys(this.cache).forEach(url => {
      this.removeFromCache(url);
    });
  }

  getCacheStats() {
    return {
      totalSize: this.currentCacheSize,
      totalVideos: Object.keys(this.cache).length,
      connectionSpeed: this.connectionSpeed
    };
  }
}

// Export singleton instance
export const videoPreloader = new VideoPreloader();

// Export utility functions
export const preloadThemeVideos = (videos: { dark: string; light: string }[], isDark: boolean) => {
  videoPreloader.preloadVideosByTheme(videos, isDark, { priority: 'medium' });
};

export const getCachedVideo = (url: string) => {
  return videoPreloader.getCachedVideoUrl(url);
}; 